import React, { useEffect, useMemo, useState } from "react";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type GetCampaign_me_adObject } from "@north-beam/nb-client-side";
import { Checkbox } from "@shared/checkbox";
import { Select } from "@shared/selects";

import { type Level } from "../../types";

import { Preview } from "./preview";
import { ViewInCreativeAnalyticsButton } from "./view-in-creative-analytics-button";

export interface Option {
  label: string;
  value: string;
  status: string;
}

function getOptionsForAdsets(
  adObject: GetCampaign_me_adObject,
  adObjectLevel: Level,
): Option[] {
  const adsets = adObject.hierarchy.adsets;

  if (adObjectLevel === "campaign") {
    return adsets.map((e: any) => ({
      label: e.name,
      value: e.id,
      status: e.status,
    }));
  }

  if (adObjectLevel === "adset") {
    return [
      { label: adObject.name, value: adObject.id, status: adObject.status },
    ];
  }

  for (const adset of adsets) {
    for (const ad of adset.ads) {
      if (ad.id === adObject.id) {
        return [{ label: adset.name, value: adset.id, status: adset.status }];
      }
    }
  }

  return [];
}

function getOptionsForAds(
  adObject: GetCampaign_me_adObject,
  adObjectLevel: Level,
  adset: ReturnType<typeof getOptionsForAdsets>[0],
  hidePausedAds: boolean,
): Option[] {
  if (adObjectLevel === "ad") {
    return [
      { label: adObject.name, value: adObject.id, status: adObject.status },
    ];
  }

  for (const e of adObject.hierarchy.adsets) {
    if (e.id === adset.value) {
      const options = e.ads.map((v: any) => ({
        label: v.name,
        value: v.id,
        status: v.status,
      })) as Option[];

      return hidePausedAds
        ? options.filter((e) => e.status === "Active")
        : options;
    }
  }

  return [];
}

interface Props {
  adObject: GetCampaign_me_adObject;
  adObjectLevel: Level;
}

const SUPPORTED_CREATIVE_PLATFORMS = [
  "Google Ads",
  "Google Shopping",
  "Facebook Ads",
  "Facebook Organic",
  "Instagram Organic",
  "Instagram Shop",
  "TikTok",
  "TikTok Organic",
  "Snapchat Ads",
  "YouTube Ads",
  "YouTube Organic",
  "Microsoft Ads",
];

export const Creatives = ({ adObject, adObjectLevel }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const optionsForAdsets = useMemo(
    () => getOptionsForAdsets(adObject, adObjectLevel),
    [adObject, adObjectLevel],
  );

  const [adset, setAdset] = useState(optionsForAdsets[0]);

  const [hidePausedAds, setHidePausedAds] = useState(false);

  const optionsForAds = useMemo(
    () => getOptionsForAds(adObject, adObjectLevel, adset, hidePausedAds),
    [adObject, adObjectLevel, adset, hidePausedAds],
  );

  const [ad, setAd] = useState<Option | undefined>(optionsForAds[0]);

  useEffect(() => {
    setAd(optionsForAds[0]);
  }, [optionsForAds]);

  const labelPlatform = useMemo(() => {
    const platform = adObject?.labelsDigest?.find(
      (i) => i.key === "Platform (Northbeam)",
    );
    return platform?.value;
  }, [adObject.labelsDigest]);

  if (!labelPlatform || !SUPPORTED_CREATIVE_PLATFORMS.includes(labelPlatform)) {
    return (
      <div className="mt-6">
        Unfortunately we do not currently support Creative Previews from{" "}
        {labelPlatform}.
      </div>
    );
  }

  return (
    <>
      {adObjectLevel === "campaign" && (
        <div className="mt-6">
          <div className="text-xs font-bold tracking-wider mb-1.5">ADSET</div>
          <Select
            options={optionsForAdsets}
            value={adset?.value}
            onChange={(value) => {
              prettyLogEventPage({
                domain: "Flyout",
                action: "onSelect",
                additionalActionInfo: "Select adset",
                data: {
                  pageContext: { adset: value },
                },
              });

              const adset = optionsForAdsets.find((e) => e.value === value)!;
              setAdset(adset);
            }}
          />
        </div>
      )}
      {(adObjectLevel === "campaign" || adObjectLevel === "adset") && (
        <div className={adObjectLevel === "campaign" ? "mt-2" : "mt-6"}>
          <div className="text-xs font-bold tracking-wider mb-1.5">AD</div>
          <Select
            options={optionsForAds}
            value={ad?.value}
            onChange={(value) => {
              prettyLogEventPage({
                domain: "Flyout",
                action: "onSelect",
                additionalActionInfo: "Select ad",
                data: {
                  pageContext: { ad: value },
                },
              });

              const ad = optionsForAds.find((e) => e.value === value)!;
              setAd(ad);
            }}
          />
          <div className="mt-2">
            <Checkbox
              label="Hide paused Ads"
              value={hidePausedAds}
              onChange={(value) => setHidePausedAds(value)}
            />
          </div>
        </div>
      )}
      <div className="mt-8">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold m-0">Creatives</h3>
          {["Facebook", "Snapchat", "TikTok"].includes(adObject.platform) && (
            <ViewInCreativeAnalyticsButton query={adObject.name} />
          )}
        </div>
      </div>
      <div className="mt-8">
        {!ad && <div>No ads available.</div>}
        {ad && <Preview ad={ad} />}
      </div>
    </>
  );
};
